import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Save, RefreshCw, Users, MapPin, Camera, X } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import DoctorAvatar from "@/components/DoctorAvatar";

export default function DoctorMapping() {
  const [branches, setBranches] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [dirty, setDirty] = useState({}); // username -> Set(branches)
  const [savingUser, setSavingUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [bRes, dRes] = await Promise.all([
        api.get("/branches"),
        api.get("/doctors", { params: { include_shared: true } }),
      ]);
      setBranches(bRes.data);
      setDoctors(dRes.data);
      setDirty({});
    } catch (err) {
      toast.error(formatApiError(err, "Could not load"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const getBranches = (d) => dirty[d.username] ?? d.branches ?? [];

  const toggle = (d, code) => {
    const current = getBranches(d);
    const next = current.includes(code) ? current.filter((c) => c !== code) : [...current, code];
    setDirty((prev) => ({ ...prev, [d.username]: next }));
  };

  const save = async (d) => {
    const next = dirty[d.username];
    if (!next) return;
    setSavingUser(d.username);
    try {
      await api.patch(`/doctors/${d.username}/branches`, { branches: next });
      toast.success(`Saved · ${d.name}`);
      setDoctors((docs) => docs.map((x) => (x.username === d.username ? { ...x, branches: next } : x)));
      setDirty((prev) => {
        const copy = { ...prev };
        delete copy[d.username];
        return copy;
      });
    } catch (err) {
      toast.error(formatApiError(err, "Save failed"));
    } finally {
      setSavingUser(null);
    }
  };

  return (
    <div className="max-w-6xl mx-auto animate-fade-up" data-testid="doctor-mapping-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.28em] text-[#5C5652] mb-3 ornament-dot">
            Superadmin · Doctor mapping
          </div>
          <h1 className="font-display text-3xl sm:text-4xl text-forest mb-1">
            Assign doctors to branches
          </h1>
          <p className="text-[#5C5652] max-w-2xl">
            Nurses will only see doctors from the branch they&apos;re working at when routing
            patients. Public queue displays only doctors mapped to each branch.
          </p>
        </div>
        <button
          onClick={load}
          data-testid="mapping-refresh"
          className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-sand bg-white hover:bg-[#F9F6F0] transition-colors text-sm font-semibold"
        >
          <RefreshCw className="h-4 w-4" strokeWidth={1.75} />
          Refresh
        </button>
      </div>

      <div className="mb-4 flex gap-3">
        <StatMini label="Doctors" value={doctors.length} icon={Users} />
        <StatMini label="Branches" value={branches.length} icon={MapPin} />
      </div>

      {loading ? (
        <div className="p-8 text-sm text-[#5C5652]">Loading…</div>
      ) : (
        <div className="bg-white border border-sand rounded-lg overflow-hidden">
          <div className="grid grid-cols-[auto_1fr_auto_auto] items-center gap-4 px-6 py-3 bg-[#F9F6F0] border-b border-sand text-[10px] font-bold uppercase tracking-wider text-[#5C5652]">
            <div>Photo</div>
            <div>Doctor</div>
            <div className="text-center">Branches</div>
            <div className="text-right">Actions</div>
          </div>
          <ul className="divide-y divide-[#E8E3D9]" data-testid="doctor-mapping-list">
            {doctors.map((d) => {
              const selected = getBranches(d);
              const isDirty = !!dirty[d.username];
              return (
                <li key={d.username} className="grid grid-cols-[auto_1fr_auto_auto] items-start gap-4 px-6 py-5">
                  <DoctorPhotoCell doctor={d} onUpdated={(next) => setDoctors((docs) => docs.map((x) => (x.username === d.username ? { ...x, avatar_data_url: next } : x)))} />
                  <div>
                    <div className="font-semibold text-[#2A2624]">{d.name}</div>
                    <div className="text-[10px] uppercase tracking-wider text-[#5C5652]">
                      {d.username}
                      {d.is_shared && <span className="ml-2 text-[#B57F1D]">· shared</span>}
                    </div>
                    {selected.length === 0 && (
                      <div className="mt-1 text-xs text-[#C25946]">
                        No branches — patients cannot be routed to this doctor.
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1.5" data-testid={`branches-${d.username}`}>
                    {branches.map((b) => {
                      const isOn = selected.includes(b.code);
                      return (
                        <button
                          key={b.code}
                          onClick={() => toggle(d, b.code)}
                          data-testid={`toggle-${d.username}-${b.code}`}
                          className={`h-8 px-2.5 rounded-md text-xs font-bold border transition-colors ${
                            isOn
                              ? "bg-forest text-white border-forest"
                              : "border-sand text-[#5C5652] hover:bg-[#F9F6F0]"
                          }`}
                          title={b.name}
                        >
                          {b.code}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    onClick={() => save(d)}
                    disabled={!isDirty || savingUser === d.username}
                    data-testid={`save-${d.username}`}
                    className="h-9 px-3 rounded-lg text-sm font-semibold inline-flex items-center gap-1.5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed bg-forest text-white hover:bg-forest-hover"
                  >
                    <Save className="h-3.5 w-3.5" strokeWidth={1.75} />
                    {savingUser === d.username ? "Saving…" : isDirty ? "Save" : "Saved"}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

function StatMini({ label, value, icon: Icon }) {
  return (
    <div className="p-4 rounded-lg bg-white border border-sand min-w-[140px]">
      <div className="flex items-center gap-2 text-[#5C5652] text-[10px] font-bold uppercase tracking-[0.22em] mb-2">
        <Icon className="h-3.5 w-3.5" strokeWidth={1.75} />
        {label}
      </div>
      <div className="font-display text-3xl text-forest">{value}</div>
    </div>
  );
}

function DoctorPhotoCell({ doctor, onUpdated }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const pick = () => fileRef.current?.click();

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Image too large (max 2 MB)");
      return;
    }
    const reader = new FileReader();
    reader.onload = async () => {
      const dataUrl = String(reader.result);
      setBusy(true);
      try {
        await api.patch(`/doctors/${doctor.username}/avatar`, { avatar_data_url: dataUrl });
        toast.success(`Photo updated · ${doctor.name}`);
        onUpdated(dataUrl);
      } catch (err) {
        toast.error(formatApiError(err, "Upload failed"));
      } finally {
        setBusy(false);
      }
    };
    reader.onerror = () => toast.error("Could not read the file");
    reader.readAsDataURL(file);
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/doctors/${doctor.username}/avatar`);
      toast.success(`Photo removed · ${doctor.name}`);
      onUpdated(null);
    } catch (err) {
      toast.error(formatApiError(err, "Remove failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative group" data-testid={`photo-cell-${doctor.username}`}>
      <DoctorAvatar doctor={doctor} size={56} testid={`avatar-${doctor.username}`} />
      <button
        onClick={pick}
        disabled={busy}
        data-testid={`photo-upload-${doctor.username}`}
        title="Upload photo"
        className="absolute -bottom-1 -right-1 h-6 w-6 rounded-full bg-forest text-white border-2 border-white flex items-center justify-center hover:bg-forest-hover disabled:opacity-60"
      >
        <Camera className="h-3 w-3" strokeWidth={2} />
      </button>
      {doctor.avatar_data_url && (
        <button
          onClick={remove}
          disabled={busy}
          data-testid={`photo-remove-${doctor.username}`}
          title="Remove photo"
          className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-[#C25946] text-white border-2 border-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <X className="h-2.5 w-2.5" strokeWidth={3} />
        </button>
      )}
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => upload(e.target.files?.[0])}
      />
    </div>
  );
}
